import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { http, getErr } from "../../services/apiClient";
import { getUser } from "../../services/authSession";
import { fmt, fmtDate, fmtDateFull, fmtRel, fmtTime, genKey, mapsUrl, openExternal, telUrl, waUrl } from "../../utils/formatters";
import { C, OPS_ROLES, SALES_ROLES, urgColor } from "../../utils/statusMappings";
import { Badge, Btn, Card, Empty, Input, Modal, Row, SH, Skeleton, StatusBadge, Tabs, Toast, useToast, renderIcon } from "../../components/legacy-primitives";
import { useCharge, useCustSrch, useMemory, useOrder, useUsers } from "../../hooks/queries";

export const PIPE = [
  { key: "CONFIRMED", label: "Confirmed", icon: "check" },
  { key: "IN_PRODUCTION", label: "Baking", icon: "chef-hat" },
  { key: "READY", label: "Ready", icon: "cake-slice" },
  { key: "OUT_FOR_DELIVERY", label: "Delivery", icon: "truck" },
  { key: "DELIVERED", label: "Delivered", icon: "map-pin" },
];

export const StatusPipeline = ({ order }: any) => {
  const idx = PIPE.findIndex(s => s.key === order?.status);
  const ai = idx < 0 ? 1 : idx;
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "14px 0", overflowX: "auto" }}>
      {PIPE.map((s, i) => {
        const done = i < ai, curr = i === ai;
        const col = C.STATUS[s.key]?.fg || C.light;
        return (
          <div key={s.key} style={{ display: "flex", alignItems: "center", flex: i < PIPE.length - 1 ? 1 : undefined }}>
            <div style={{ display: "flex", flexDirection: "column" as any, alignItems: "center", gap: 5, minWidth: 60 }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: done ? col : curr ? col + "18" : C.bg, border: `2px solid ${curr || done ? col : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, boxShadow: curr ? `0 0 0 4px ${col}18` : "none", transition: "all .3s" }}>
                {done ? <span style={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>✓</span> : <span style={{ opacity: curr ? 1 : .3, display: "inline-flex", color: col }}>{renderIcon(s.icon, 15)}</span>}
              </div>
              <span style={{ fontSize: 9, fontWeight: curr ? 800 : 500, color: curr || done ? col : C.light, textAlign: "center" as any, whiteSpace: "nowrap" as any }}>{s.label}</span>
            </div>
            {i < PIPE.length - 1 && <div style={{ flex: 1, height: 2, background: done ? col : C.border, margin: "0 3px", marginBottom: 18, transition: "background .3s", minWidth: 16 }} />}
          </div>
        );
      })}
      {["FLAGGED", "PENDING_APPROVAL", "DELIVERY_FAILED", "CANCELLED"].includes(order?.status) && <div style={{ marginLeft: 8 }}><StatusBadge status={order.status} /></div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// 6. ACTION MODALS
// ═══════════════════════════════════════════════════════════

export const CancelModal = ({ order, onClose, onSuccess }: any) => {
  const { t, show } = useToast();
  const [reason, setReason] = useState(""); const [refund, setRefund] = useState("NO"); const [amt, setAmt] = useState(""); const [loading, setLoading] = useState(false);
  const qc = useQueryClient();
  return <Modal title="Cancel Order" subtitle={order.orderNumber} onClose={onClose} width={460}>
    {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
    <div style={{ background: "#FDEAEA", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: C.danger }}>⚠ This cannot be undone.</div>
    <Input label="Reason" value={reason} onChange={setReason} options={["Customer request", "Wrong order details", "Payment issue", "Business decision", "Duplicate", "Other"]} required />
    {order.advancePaidPaise > 0 && <Input label="Advance Refund?" value={refund} onChange={setRefund} options={["NO", "YES", "PARTIAL"]} />}
    {refund === "PARTIAL" && <Input label="Refund Amount (₹)" value={amt} onChange={setAmt} type="number" />}
    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
      <Btn v="ghost" onClick={onClose} sx={{ flex: 1 }}>Keep Order</Btn>
      <Btn v="danger" loading={loading} sx={{ flex: 1 }} onClick={async () => {
        if (!reason) { show("Reason required", "error"); return; }
        setLoading(true);
        try { await http.patch(`/orders/${order.id}/cancel`, { reason, refundStatus: refund, refundAmountPaise: refund === "PARTIAL" ? Math.round(parseFloat(amt) * 100) : refund === "YES" ? order.advancePaidPaise : 0 }); qc.invalidateQueries({ queryKey: ["orders"] }); show("Order cancelled"); setTimeout(() => { onSuccess?.(); onClose(); }, 1200); } catch (e: any) { show(getErr(e), "error"); } finally { setLoading(false); }
      }}>Confirm Cancel</Btn>
    </div>
  </Modal>;
};

export const RescheduleModal = ({ order, onClose, onSuccess }: any) => {
  const { t, show } = useToast();
  const [date, setDate] = useState(""); const [time, setTime] = useState(""); const [loading, setLoading] = useState(false);
  const qc = useQueryClient();
  return <Modal title="Reschedule" subtitle={order.orderNumber} onClose={onClose} width={400}>
    {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
    <div style={{ background: C.soft, borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: C.primary }}>Current: <strong>{fmtDateFull(order.deliveryDate)} at {fmtTime(order.deliveryTime)}</strong></div>
    <Input label="New Date" value={date} onChange={setDate} type="date" required />
    <Input label="New Time" value={time} onChange={setTime} type="time" required />
    <div style={{ display: "flex", gap: 8 }}>
      <Btn v="ghost" onClick={onClose} sx={{ flex: 1 }}>Cancel</Btn>
      <Btn loading={loading} sx={{ flex: 1 }} onClick={async () => {
        if (!date || !time) { show("Date and time required", "error"); return; }
        setLoading(true);
        try { await http.patch(`/orders/${order.id}/reschedule`, { deliveryDate: date, deliveryTime: time }); qc.invalidateQueries({ queryKey: ["orders"] }); show("Rescheduled"); setTimeout(() => { onSuccess?.(); onClose(); }, 1200); } catch (e: any) { show(getErr(e), "error"); } finally { setLoading(false); }
      }}>Confirm</Btn>
    </div>
  </Modal>;
};

export const RiderModal = ({ order, onClose, onSuccess }: any) => {
  const { t, show } = useToast();
  const { data: users } = useUsers();
  const riders = (users || []).filter((u: any) => u.role === "DELIVERY" && u.isActive);
  const [sel, setSel] = useState(""); const [loading, setLoading] = useState(false);
  const qc = useQueryClient();
  return <Modal title="Assign Rider" subtitle={order.orderNumber} onClose={onClose} width={420}>
    {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
    {riders.map((r: any) => (
      <div key={r.id} onClick={() => setSel(r.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: `2px solid ${sel === r.id ? C.primary : C.border}`, background: sel === r.id ? C.soft : C.surface, cursor: "pointer", marginBottom: 8, transition: "all .12s" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.primary, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 15 }}>{r.name.charAt(0)}</div>
        <div><div style={{ fontSize: 14, fontWeight: 600 }}>{r.name}</div><div style={{ fontSize: 11, color: C.mid }}>{r.email}</div></div>
        {sel === r.id && <div style={{ marginLeft: "auto", color: C.primary, fontSize: 18 }}>✓</div>}
      </div>
    ))}
    {!riders.length && <div style={{ padding: "20px 0", textAlign: "center" as any, color: C.mid }}>No active delivery staff</div>}
    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
      <Btn v="ghost" onClick={onClose} sx={{ flex: 1 }}>Cancel</Btn>
      <Btn loading={loading} disabled={!sel} sx={{ flex: 1 }} onClick={async () => {
        setLoading(true);
        try { await http.post("/delivery/assignments", { orderId: order.id, riderId: sel }); qc.invalidateQueries({ queryKey: ["orders"] }); show("Rider assigned"); setTimeout(() => { onSuccess?.(); onClose(); }, 1200); } catch (e: any) { show(getErr(e), "error"); } finally { setLoading(false); }
      }}>Assign</Btn>
    </div>
  </Modal>;
};

// ═══════════════════════════════════════════════════════════
// 7. ORDER COMMAND CENTER
// Single screen. Full context. All actions in one place.
// ═══════════════════════════════════════════════════════════

export const OrderCommandCenter = ({ orderId, onClose }: any) => {
  const { data: order, refetch } = useOrder(orderId);
  const { data: mem } = useMemory(order?.customerId || "");
  const qc = useQueryClient();
  const { t, show } = useToast();
  const [tab, setTab] = useState("Overview");
  const [flagCat, setFlagCat] = useState(""); const [flagNotes, setFlagNotes] = useState("");
  const [feedback, setFeedback] = useState("");
  const [payMode, setPayMode] = useState("CASH"); const [payRef, setPayRef] = useState("");
  const [ack, setAck] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showResched, setShowResched] = useState(false);
  const [showRider, setShowRider] = useState(false);
  const user = getUser();

  if (!order) return <Modal title="Loading…" onClose={onClose}>{[1, 2, 3].map(i => <Skeleton key={i} />)}</Modal>;

  const doStatus = async (status: string, notes = "") => { try { await http.patch(`/orders/${orderId}/status`, { status, notes }); qc.invalidateQueries({ queryKey: ["orders"] }); refetch(); show(`Status: ${status.replace(/_/g, " ")}`); } catch (e: any) { show(getErr(e), "error"); } };
  const doFlag   = async () => { if (!flagCat) return; try { await http.post(`/orders/${orderId}/flag`, { category: flagCat, notes: flagNotes }); qc.invalidateQueries({ queryKey: ["orders"] }); refetch(); show("Flag raised — Sales notified"); setFlagCat(""); setFlagNotes(""); } catch (e: any) { show(getErr(e), "error"); } };
  const doPay    = async () => { if (payMode === "UPI" && !payRef) { show("UPI ref required", "error"); return; } try { await http.patch(`/orders/${orderId}/delivery-payment`, { mode: payMode, reference: payRef, amountPaise: order.balanceDuePaise }); qc.invalidateQueries({ queryKey: ["orders"] }); refetch(); show("Payment recorded ✓"); } catch (e: any) { show(getErr(e), "error"); } };
  const doFbk    = async () => { if (!feedback) return; try { await http.post(`/orders/${orderId}/feedback`, { feedbackText: feedback }); show("Feedback saved"); setFeedback(""); } catch (e: any) { show(getErr(e), "error"); } };
  const doAck    = async () => { try { await http.patch(`/orders/${orderId}/allergen-acknowledge`); refetch(); setAck(true); show("Allergen acknowledged"); } catch (e: any) { show(getErr(e), "error"); } };
  const doResolve = async (fid: string) => { try { await http.patch(`/orders/${orderId}/flag/${fid}/resolve`, { resolutionNotes: "Resolved" }); qc.invalidateQueries({ queryKey: ["orders"] }); refetch(); show("Flag resolved"); } catch (e: any) { show(getErr(e), "error"); } };

  const ACTS: any = {
    OWNER: ["CONFIRMED", "IN_PRODUCTION", "READY", "OUT_FOR_DELIVERY", "DELIVERED", "COLLECTED"],
    MASTER_ADMIN: ["CONFIRMED", "IN_PRODUCTION", "READY", "OUT_FOR_DELIVERY", "DELIVERED", "COLLECTED"],
    SUPER_ADMIN: ["CONFIRMED", "IN_PRODUCTION", "READY", "OUT_FOR_DELIVERY", "DELIVERED", "COLLECTED"],
    OPERATIONS_MANAGER: ["CONFIRMED", "IN_PRODUCTION", "READY", "OUT_FOR_DELIVERY", "DELIVERED", "COLLECTED"],
    PRODUCTION: ["IN_PRODUCTION", "READY"],
    DELIVERY: ["OUT_FOR_DELIVERY", "DELIVERY_FAILED"],
    SALES: [],
    SALES_MANAGER: [],
    ACCOUNTS: [],
    MARKETING: []
  };

  return (
    <Modal title={order.orderNumber} subtitle={`${order.customerName} · ${order.occasion} · ${fmtDateFull(order.deliveryDate)} ${fmtTime(order.deliveryTime)}`} onClose={onClose} width={720}>
      {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}
      <StatusPipeline order={order} />
      <Tabs items={["Overview", "Cake", "Pricing", "Timeline", "Actions"]} active={tab} onChange={setTab} />

      {tab === "Overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <SH title="Customer" />
            <div style={{ background: C.soft, borderRadius: 12, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 3 }}>{order.customerName}</div>
              <div style={{ fontSize: 13, color: C.mid, marginBottom: 10 }}>+91 {order.customerPhone}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <a href={waUrl(order.customerPhone)} target="_blank" rel="noreferrer" style={{ background: "#25D366", color: "#fff", padding: "7px 14px", borderRadius: 9, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>💬 WhatsApp</a>
                <a href={telUrl(order.customerPhone)} style={{ background: C.surface, color: C.text, padding: "7px 12px", borderRadius: 9, fontSize: 12, fontWeight: 700, textDecoration: "none", border: `1px solid ${C.border}` }}>📞 Call</a>
              </div>
            </div>
            <Row label="Occasion" value={order.occasion} />
            <Row label="Source" value={order.source} />
            {order.isFlagged && (
              <div style={{ marginTop: 12, background: "#FDEAEA", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.danger, marginBottom: 6 }}>🚩 Active Flags</div>
                {(order.flags || []).filter((f: any) => !f.resolvedAt).map((f: any) => (
                  <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div><div style={{ fontSize: 12, fontWeight: 600, color: C.danger }}>{f.category}</div>{f.notes && <div style={{ fontSize: 11, color: C.mid }}>{f.notes}</div>}</div>
                    {SALES_ROLES.includes(user?.role) && <Btn v="ghost" size="xs" onClick={() => doResolve(f.id)}>Resolve</Btn>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            {mem && (
              <div style={{ background: `linear-gradient(135deg,${C.soft},${C.surface})`, borderRadius: 14, padding: 14 }}>
                <SH title="🧠 Customer Intelligence" />
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5, marginBottom: 10, fontStyle: "italic" }}>{mem.contextualGreeting}</div>
                {mem.insights?.nextOccasionAlert && <div style={{ background: C.warning + "20", color: C.warning, fontSize: 12, fontWeight: 700, padding: "6px 10px", borderRadius: 8, marginBottom: 10 }}>📅 {mem.insights.nextOccasionAlert}</div>}
                <Row label="Fav Flavors" value={(mem.favoriteFlavors || []).slice(0, 2).join(", ")} />
                <Row label="Avg Order"   value={fmt(mem.averageOrderValuePaise || 0)} />
                <Row label="Churn Risk"  value={<span style={{ color: mem.churnRiskScore > 60 ? C.danger : C.success, fontWeight: 700 }}>{mem.churnRiskScore}%</span>} />
                {(mem.emotionalTags || []).length > 0 && <div style={{ marginTop: 10, display: "flex", gap: 5, flexWrap: "wrap" as any }}>{mem.emotionalTags.map((tg: string) => <Badge key={tg} color={C.primary}>{tg}</Badge>)}</div>}
                {mem.retentionAlert && <div style={{ marginTop: 10, background: "#FDEAEA", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, color: C.danger }}>{mem.retentionAlert}</div>}
                {mem.whatsappContext && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.mid, textTransform: "uppercase" as any, marginBottom: 6 }}>Suggested WhatsApp</div>
                    <div style={{ background: C.bg, borderRadius: 8, padding: "10px 12px", fontSize: 12, lineHeight: 1.5, fontStyle: "italic", marginBottom: 8 }}>"{mem.whatsappContext.slice(0, 120)}…"</div>
                    <a href={waUrl(order.customerPhone, mem.whatsappContext)} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "#25D366", color: "#fff", borderRadius: 9, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>Send This →</a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "Cake" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <SH title="Cake Specs" />
            <Row label="Weight"  value={`${order.cakeWeightLbs} lbs`} />
            <Row label="Flavor"  value={order.flavor} />
            <Row label="Finish"  value={order.finishType} />
            <Row label="Tiers"   value={order.layers} />
            <Row label="Message" value={order.messageOnCake} />
            {order.hasAllergenFlag && (
              <div style={{ marginTop: 12, background: "#FEF3E2", border: `2px solid ${C.warning}40`, borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.warning, marginBottom: 8 }}>⚠ ALLERGEN / SPECIAL INSTRUCTIONS</div>
                <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 12 }}>{order.specialInstructions}</div>
                {user?.role === "PRODUCTION" && !order.allergenAcknowledgedAt && !ack && (
                  <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
                    <input type="checkbox" onChange={e => { if (e.target.checked) doAck(); }} style={{ marginTop: 3 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.warning }}>I confirm I have read all allergen instructions</span>
                  </label>
                )}
                {(order.allergenAcknowledgedAt || ack) && <div style={{ fontSize: 11, color: C.success, fontWeight: 700 }}>✓ Acknowledged</div>}
              </div>
            )}
          </div>
          <div>
            <SH title="Delivery" />
            <Row label="Type"    value={order.deliveryType === "SELF_PICKUP" ? "🏪 Self Pickup" : "🚚 Home Delivery"} />
            <Row label="Date"    value={fmtDateFull(order.deliveryDate)} />
            <Row label="Time"    value={fmtTime(order.deliveryTime)} />
            <Row label="Address" value={order.deliveryAddress} />
            <Row label="Recipient" value={order.recipientName ? `${order.recipientName} · ${order.recipientPhone}` : null} />
            <Row label="Distance" value={order.deliveryDistanceKm ? `${order.deliveryDistanceKm} km` : null} />
            <Row label="Rider"   value={order.assignment?.rider?.name} />
            {order.deliveryAddress && <a href={mapsUrl(order.deliveryAddress)} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", background: C.primary, color: "#fff", borderRadius: 10, fontSize: 13, fontWeight: 700, textDecoration: "none", marginTop: 14 }}>📍 Open Google Maps</a>}
            {[...OPS_ROLES, "SALES_MANAGER"].includes(user?.role) && !order.assignedRiderId && <Btn v="secondary" size="sm" style={{ marginTop: 8 }} onClick={() => setShowRider(true)}>Assign Rider</Btn>}
          </div>
        </div>
      )}

      {tab === "Pricing" && (
        <div>
          <div style={{ background: C.bg, borderRadius: 14, padding: 18, marginBottom: 16 }}>
            {[["Base Price", fmt(order.basePricePaise)], order.discountPaise > 0 ? ["Discount", `− ${fmt(order.discountPaise)}`] : null, order.deliveryChargePaise > 0 ? ["Delivery", fmt(order.deliveryChargePaise)] : null, ["GST (5%)", fmt(order.gstPaise)]].filter(Boolean).map(([l, v]: any, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}><span style={{ color: C.mid }}>{l}</span><span>{v}</span></div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontSize: 18, fontWeight: 900 }}><span>Total</span><span style={{ color: C.primary }}>{fmt(order.totalPaise)}</span></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ background: "#E6F5EC", borderRadius: 12, padding: 14, textAlign: "center" as any }}>
              <div style={{ fontSize: 11, color: C.success, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" as any }}>Advance Paid</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: C.success }}>{fmt(order.advancePaidPaise)}</div>
            </div>
            <div style={{ background: order.balanceDuePaise > 0 ? C.soft : "#E6F5EC", borderRadius: 12, padding: 14, textAlign: "center" as any }}>
              <div style={{ fontSize: 11, color: order.balanceDuePaise > 0 ? C.primary : C.success, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" as any }}>Balance Due</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: order.balanceDuePaise > 0 ? C.primary : C.success }}>{order.balanceDuePaise > 0 ? fmt(order.balanceDuePaise) : "Paid ✓"}</div>
            </div>
          </div>
        </div>
      )}

      {tab === "Timeline" && (
        <div>
          <SH title="Status History" />
          {(order.statusLogs || []).map((l: any, i: number, arr: any[]) => (
            <div key={i} style={{ display: "flex", gap: 14, marginBottom: 2 }}>
              <div style={{ display: "flex", flexDirection: "column" as any, alignItems: "center", width: 28 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: C.STATUS[l.status]?.fg || C.light, flexShrink: 0 }} />
                {i < arr.length - 1 && <div style={{ width: 2, flex: 1, background: C.border, minHeight: 20 }} />}
              </div>
              <div style={{ flex: 1, paddingBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.STATUS[l.status]?.fg || C.text }}>{l.status?.replace(/_/g, " ")}</div>
                {l.notes && <div style={{ fontSize: 12, color: C.mid, marginTop: 1 }}>{l.notes}</div>}
                <div style={{ fontSize: 11, color: C.light, marginTop: 2 }}>{l.createdAt ? fmtRel(l.createdAt) : ""}</div>
              </div>
            </div>
          ))}
          {order.feedback && <><SH title="Customer Feedback" /><div style={{ background: C.bg, borderRadius: 10, padding: 12, fontSize: 13, fontStyle: "italic" }}>"{order.feedback.feedbackText}"</div></>}
        </div>
      )}

      {tab === "Actions" && (
        <div style={{ display: "flex", flexDirection: "column" as any, gap: 16 }}>
          {/* Status buttons */}
          {(ACTS[user?.role] || []).length > 0 && (
            <div style={{ background: C.bg, borderRadius: 14, padding: 16 }}>
              <SH title="Update Status" />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as any }}>
                {(ACTS[user?.role] || []).map((s: string) => <Btn key={s} size="sm" v={["CANCELLED", "DELIVERY_FAILED"].includes(s) ? "danger" : "primary"} onClick={() => doStatus(s)}>{s.replace(/_/g, " ")}</Btn>)}
              </div>
            </div>
          )}
          {/* Flag — Production */}
          {[...OPS_ROLES, "PRODUCTION"].includes(user?.role) && (
            <div style={{ background: C.bg, borderRadius: 14, padding: 16 }}>
              <SH title="Raise Flag" />
              <Input label="Category" value={flagCat} onChange={setFlagCat} options={["Ingredient unavailable", "Design unclear", "Equipment issue", "Staff shortage", "Other"]} required />
              <Input label="Notes" value={flagNotes} onChange={setFlagNotes} rows={2} />
              <Btn v="danger" size="sm" onClick={doFlag} disabled={!flagCat} icon="triangle-alert">Raise Flag — Sales Notified</Btn>
            </div>
          )}
          {/* Balance payment — Delivery */}
          {[...OPS_ROLES, "DELIVERY"].includes(user?.role) && order.status === "OUT_FOR_DELIVERY" && order.balanceDuePaise > 0 && (
            <div style={{ background: C.bg, borderRadius: 14, padding: 16 }}>
              <SH title="Record Balance Payment" />
              <div style={{ fontSize: 15, fontWeight: 700, color: C.primary, marginBottom: 12 }}>Amount: {fmt(order.balanceDuePaise)}</div>
              <Input label="Mode" value={payMode} onChange={setPayMode} options={["CASH", "UPI"]} />
              {payMode === "UPI" && <Input label="UPI Reference" value={payRef} onChange={setPayRef} required placeholder="Transaction ID" />}
              <Btn onClick={doPay} disabled={payMode === "UPI" && !payRef} full>Record Payment & Mark Delivered</Btn>
            </div>
          )}
          {/* Feedback — Sales */}
          {SALES_ROLES.includes(user?.role) && ["DELIVERED", "COLLECTED"].includes(order.status) && !order.feedback && (
            <div style={{ background: C.bg, borderRadius: 14, padding: 16 }}>
              <SH title="Customer Feedback" />
              <Input label="Feedback from call or WhatsApp" value={feedback} onChange={setFeedback} rows={3} placeholder="What did the customer say?" />
              <Btn v="secondary" onClick={doFbk} disabled={!feedback}>Save Feedback</Btn>
            </div>
          )}
          {/* Cancel / Reschedule */}
          {SALES_ROLES.includes(user?.role) && !["CANCELLED", "DELIVERED", "COLLECTED", "OUT_FOR_DELIVERY"].includes(order.status) && (
            <div style={{ display: "flex", gap: 8 }}>
              <Btn v="ghost" size="sm" onClick={() => setShowResched(true)}>Reschedule</Btn>
              <Btn v="danger" size="sm" onClick={() => setShowCancel(true)}>Cancel Order</Btn>
            </div>
          )}
        </div>
      )}

      {showCancel   && <CancelModal     order={order} onClose={() => setShowCancel(false)}  onSuccess={() => { refetch(); onClose(); }} />}
      {showResched  && <RescheduleModal order={order} onClose={() => setShowResched(false)} onSuccess={refetch} />}
      {showRider    && <RiderModal      order={order} onClose={() => setShowRider(false)}   onSuccess={refetch} />}
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════
// 8. ORDER CREATION FORM (4-step)
// ═══════════════════════════════════════════════════════════

export const NewOrderForm = ({ onClose, onSuccess, inquiry = null }: any) => {
  const [step, setStep] = useState(1);

  const [F, setF] = useState(() => {
    const sourceMap: Record<string, string> = {
      WhatsApp: "WhatsApp Referral",
      Instagram: "Instagram Organic",
      Facebook: "Facebook Ad",
      Google: "Google",
      "Walk-in": "Walk-in",
      Referral: "WhatsApp Referral",
      Other: "Other",
    };

    return {
      name: inquiry?.customerName || "",
      phone: String(inquiry?.phone || "").replace(/\D/g, "").slice(-10),
      email: inquiry?.customer?.email || "",
      source: sourceMap[inquiry?.source] || inquiry?.source || "Instagram Organic",
      occasion: inquiry?.occasion || "",
      date: "",
      time: "",
      dtype: "HOME_DELIVERY",
      address: "",
      rname: "",
      rphone: "",
      weight: "",
      flavor: "",
      layers: "1",
      finish: "",
      design: "",
      msg: "",
      special: inquiry?.notes || "",
      price: "",
      disc: "0",
      advance: "",
      mode: "CASH",
      ref: "",
    };
  });
  const set = (k: string) => (v: string) => setF(f => ({ ...f, [k]: v }));
  const [ikey] = useState(genKey());
  const [loading, setLoading] = useState(false);
  const [errs, setErrs] = useState<any>({});
  const { t, show } = useToast();
  const { data: hits } = useCustSrch(F.phone);
  const { data: charge } = useCharge(F.dtype === "HOME_DELIVERY" ? F.address : "");
  const qc = useQueryClient();
  useEffect(() => { if (hits?.[0] && F.phone.length === 10) { set("name")(hits[0].name); if (hits[0].email) set("email")(hits[0].email); } }, [hits]);

  const baseP = parseFloat(F.price || "0") * 100;
  const discP = parseFloat(F.disc || "0") > 0 ? Math.round(baseP * parseFloat(F.disc) / 100) : 0;
  const delivP = F.dtype === "HOME_DELIVERY" ? (charge?.chargePaise || 0) : 0;
  const gst = Math.round((baseP - discP + delivP) * 0.05);
  const total = baseP - discP + delivP + gst;

  const val = (s: number) => {
    const e: any = {};
    if (s === 1) {
      if (!F.name) e.name = "Required";
      const phone = F.phone.replace(/\D/g, "");

      if (!/^[6-9]\d{9}$/.test(phone)) {
        e.phone = "Valid 10-digit Indian mobile number";
      }
    }
    if (s === 2) { if (!F.occasion) e.occ = "Required"; if (!F.date) e.date = "Required"; if (!F.time) e.time = "Required"; if (F.dtype === "HOME_DELIVERY" && !F.address) e.addr = "Required"; }
    if (s === 3) { if (!F.weight) e.wt = "Required"; if (!F.flavor) e.flv = "Required"; if (!F.finish) e.fin = "Required"; }
    if (s === 4) { if (!F.price) e.price = "Required"; if (!F.advance) e.adv = "Required"; }
    setErrs(e); return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!val(4)) return; setLoading(true);
    try {
      await http.post("/orders", { inquiryId: inquiry?.id || undefined, customerName: F.name, customerPhone: F.phone.replace(/\D/g, "").slice(-10), customerEmail: F.email || undefined, source: F.source, occasion: F.occasion, deliveryDate: F.date, deliveryTime: F.time, deliveryType: F.dtype, deliveryAddress: F.address || undefined, recipientName: F.rname || undefined, recipientPhone: F.rphone || undefined, cakeWeightLbs: parseFloat(F.weight), flavor: F.flavor, layers: parseInt(F.layers) || 1, finishType: F.finish, messageOnCake: F.msg || undefined, specialInstructions: F.special || undefined, basePricePaise: Math.round(parseFloat(F.price) * 100), discountValue: parseFloat(F.disc) || 0, advancePaidPaise: Math.round(parseFloat(F.advance) * 100), paymentMode: F.mode, paymentReference: F.ref || undefined }, { headers: { "X-Idempotency-Key": ikey } });
      qc.invalidateQueries({ queryKey: ["orders"] }); show("Order confirmed ✓"); setTimeout(() => { onSuccess?.(); onClose(); }, 1500);
    } catch (e: any) { show(getErr(e), "error"); } finally { setLoading(false); }
  };

  const STEPS = ["Customer", "Delivery", "Cake", "Pricing"];

  return (
    <Modal title="New Order" subtitle="Complete all steps to confirm" onClose={onClose} width={620}>
      {t && <Toast msg={t.msg} type={t.type} onClose={() => {}} />}

      {Object.keys(errs).length > 0 && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            background: "#FFF1F2",
            border: `1px solid ${C.danger}`,
            color: C.danger,
            borderRadius: 10,
            padding: "10px 12px",
            marginBottom: 16,
            fontSize: 12,
          }}
        >
          <strong>Please fix the highlighted fields before continuing.</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {Object.entries(errs).map(([key, message]) => {
              const labels: Record<string, string> = {
                name: "Customer name",
                phone: "WhatsApp number",
                occ: "Occasion",
                date: "Delivery date",
                time: "Delivery time",
                addr: "Delivery address",
                wt: "Weight",
                flv: "Flavor",
                fin: "Finish",
                price: "Base price",
                adv: "Advance",
              };

              return (
                <li key={key}>
                  {labels[key] || key}: {String(message)}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div style={{ display: "flex", gap: 0, marginBottom: 24 }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column" as any, alignItems: "center", gap: 5 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: step > i + 1 ? C.success : step === i + 1 ? C.primary : C.bg, color: step >= i + 1 ? "#fff" : C.light, fontWeight: 700, fontSize: 13, border: `2px solid ${step > i + 1 ? C.success : step === i + 1 ? C.primary : C.border}` }}>{step > i + 1 ? "✓" : i + 1}</div>
            <span style={{ fontSize: 10, color: step === i + 1 ? C.primary : C.light, fontWeight: step === i + 1 ? 700 : 400 }}>{s}</span>
          </div>
        ))}
      </div>

      {step === 1 && <>
        {hits?.[0] && F.phone.length === 10 && <div style={{ background: C.soft, border: `1px solid ${C.accent}`, borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13, color: C.primary, fontWeight: 600 }}>Existing customer · {hits[0].totalOrderCount} orders · {fmt(hits[0].totalSpendPaise || 0)}</div>}
        <Input label="Customer Name"   value={F.name}   onChange={set("name")}   required error={errs.name} />
        <Input label="WhatsApp Number" value={F.phone}  onChange={(v: string) => set("phone")(v.replace(/\D/g, "").slice(0, 10))} required error={errs.phone} placeholder="10-digit" />
        <Input label="Email (optional)"value={F.email}  onChange={set("email")}  type="email" />
        <Input label="Source Channel"  value={F.source} onChange={set("source")} options={["Facebook Ad", "Instagram Ad", "Instagram Organic", "WhatsApp Referral", "Google", "Walk-in", "Other"]} />
      </>}

      {step === 2 && <>
        <Input label="Occasion"     value={F.occasion} onChange={set("occasion")} options={["Birthday", "Anniversary", "Wedding", "Corporate", "Baby Shower", "Other"]} required error={errs.occ} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Input label="Delivery Date" value={F.date} onChange={set("date")} type="date" required error={errs.date} />
          <Input label="Delivery Time" value={F.time} onChange={set("time")} type="time" required error={errs.time} />
        </div>
        <Input label="Delivery Type" value={F.dtype} onChange={set("dtype")} options={["HOME_DELIVERY", "SELF_PICKUP"]} />
        {F.dtype === "HOME_DELIVERY" && <>
          <Input label="Delivery Address" value={F.address} onChange={set("address")} required error={errs.addr} placeholder="Full address with PIN" />
          {charge && <div style={{ background: C.soft, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.primary, fontWeight: 700, marginTop: -8, marginBottom: 14 }}>📍 {charge.breakdown} = {fmt(charge.chargePaise)}</div>}
          <Input label="Recipient Name (if different)"  value={F.rname}  onChange={set("rname")} />
          <Input label="Recipient Phone (if different)" value={F.rphone} onChange={set("rphone")} />
        </>}
      </>}

      {step === 3 && <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Input label="Weight (lbs)" value={F.weight} onChange={set("weight")} type="number" required error={errs.wt} placeholder="e.g. 2.5" suffix="lbs" />
          <Input label="Tiers"        value={F.layers} onChange={set("layers")} type="number" />
        </div>
        <Input label="Flavor" value={F.flavor} onChange={set("flavor")} options={["Chocolate", "Vanilla", "Red Velvet", "Butterscotch", "Belgian Chocolate", "Blueberry Cheesecake", "Custom"]} required error={errs.flv} />
        <Input label="Finish" value={F.finish} onChange={set("finish")} options={["Fondant", "Whipped Cream", "Buttercream", "Floral Fondant"]} required error={errs.fin} />
        <Input label="Design Description" value={F.design} onChange={set("design")} rows={2} placeholder="Describe design. Share images via WhatsApp." />
        {F.phone && <div style={{ fontSize: 12, color: C.secondary, marginTop: -8, marginBottom: 14, cursor: "pointer", fontWeight: 600 }} onClick={() => openExternal(waUrl(F.phone))}>💬 Open WhatsApp for design images →</div>}
        <Input label="Message on Cake"    value={F.msg}     onChange={set("msg")}     placeholder="Happy Birthday Priya!" />
        <Input label="Allergens / Special"value={F.special} onChange={set("special")} rows={2} placeholder="Nut allergy, vegan, gluten-free…" />
        {F.special && <div style={{ fontSize: 11, fontWeight: 700, color: C.warning, marginTop: -8, marginBottom: 14 }}>⚠ Allergen flag will be set — Production sees this prominently</div>}
      </>}

      {step === 4 && <>
        <Input label="Base Price (₹)" value={F.price}   onChange={set("price")}   type="number" required error={errs.price} />
        <Input label="Discount (%)"   value={F.disc}    onChange={set("disc")}    type="number" suffix="%" />
        <div style={{ background: C.bg, borderRadius: 12, padding: 16, marginBottom: 14 }}>
          {[["Base", fmt(baseP)], discP > 0 ? ["Discount", `− ${fmt(discP)}`] : null, delivP > 0 ? ["Delivery", fmt(delivP)] : null, ["GST 5%", fmt(gst)]].filter(Boolean).map(([l, v]: any, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}><span style={{ color: C.mid }}>{l}</span><span>{v}</span></div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontSize: 18, fontWeight: 900 }}><span>Total</span><span style={{ color: C.primary }}>{fmt(total)}</span></div>
          <div style={{ fontSize: 12, color: C.mid, marginTop: 4 }}>30% advance = <strong style={{ color: C.primary }}>{fmt(Math.ceil(total * .3))}</strong></div>
        </div>
        <Input label="Advance Amount (₹)" value={F.advance} onChange={set("advance")} type="number" required error={errs.adv} />
        <Input label="Payment Mode"       value={F.mode}    onChange={set("mode")}    options={["CASH", "UPI", "CARD", "BANK_TRANSFER"]} />
        {["UPI", "BANK_TRANSFER"].includes(F.mode) && <Input label="Payment Reference" value={F.ref} onChange={set("ref")} required placeholder="Transaction ID" />}
      </>}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 22, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
        <Btn v="ghost" onClick={step === 1 ? onClose : () => setStep(s => s - 1)}>{step === 1 ? "Cancel" : "← Back"}</Btn>
        {step < 4 ? <Btn onClick={() => { if (val(step)) setStep(s => s + 1); }}>Next →</Btn> : <Btn onClick={submit} loading={loading}>Confirm Order</Btn>}
      </div>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════
// 9. INTELLIGENCE DASHBOARD (all 7 AI engines surfaced)
// ═══════════════════════════════════════════════════════════



// ═══════════════════════════════════════════════════════════
// 10. ALL PAGE COMPONENTS
// ═══════════════════════════════════════════════════════════

export const OrderCard = ({ order, onClick }: any) => (
  <Card onClick={onClick} urgency={order.isFlagged ? C.danger : urgColor(order.deliveryDate)}>
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span style={{ fontSize: 12, fontWeight: 800 }}>{order.orderNumber}</span><StatusBadge status={order.status} /></div>
    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{order.customerName}</div>
    <div style={{ fontSize: 12, color: C.mid, marginBottom: 6 }}>{order.cakeWeightLbs}lbs · {order.flavor} · {fmtDate(order.deliveryDate)} {fmtTime(order.deliveryTime)}</div>
    {order.hasAllergenFlag && <div style={{ fontSize: 10, fontWeight: 700, color: C.warning, background: C.warning + "15", padding: "2px 8px", borderRadius: 6, marginBottom: 6 }}>⚠ Allergen</div>}
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}><span>{fmt(order.totalPaise)}</span>{order.balanceDuePaise > 0 ? <span style={{ color: C.primary, fontWeight: 700 }}>Due: {fmt(order.balanceDuePaise)}</span> : <span style={{ color: C.success }}>Paid ✓</span>}</div>
  </Card>
);
